import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet';
import { navLinks } from '@/data/mock';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';
  const isDark = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors transition-shadow duration-300 ${
        isDark
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="relative">
              <div className={`absolute inset-0 rounded-full blur-lg scale-150 ${
                isDark ? 'bg-[#3B82A0]/30' : 'bg-[#3B82A0]/15'
              }`} />
              <div className={`absolute inset-0 rounded-full ${
                isDark ? 'shadow-[0_0_12px_2px_rgba(59,130,160,0.4)]' : 'shadow-[0_0_8px_1px_rgba(59,130,160,0.2)]'
              }`} />
              <img
                src="/logo.png"
                alt="CISO Dynamics"
                className="relative w-14 h-14 rounded-full object-cover"
              />
            </div>
            <span
              className={`text-lg tracking-[0.05em] ${
                isDark ? 'text-white' : 'text-[#0B1D33]'
              }`}
            >
              <span className="font-semibold">CISO</span> <span className="font-light">Dynamics</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[13px] font-medium tracking-wide uppercase transition-colors duration-200 ${
                  isActive(link.path)
                    ? isDark
                      ? 'text-[#3B82A0]'
                      : 'text-[#3B82A0]'
                    : isDark
                    ? 'text-white/70 hover:text-white'
                    : 'text-slate-500 hover:text-[#0B1D33]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <Link to="/contact">
              <Button
                className={`text-[13px] font-medium px-5 h-9 rounded transition-colors duration-200 ${
                  isDark
                    ? 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                    : 'bg-[#0B1D33] text-white hover:bg-[#162D4A]'
                }`}
              >
                Engage Our Experts
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button
                className={`p-2 rounded-md transition-colors ${
                  isDark ? 'text-white hover:bg-white/10' : 'text-[#0B1D33] hover:bg-slate-100'
                }`}
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white border-l border-slate-200">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col gap-6 mt-8">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="text-lg tracking-[0.05em] text-[#0B1D33]"
                >
                  <span className="font-semibold">CISO</span> <span className="font-light">Dynamics</span>
                </Link>
                <nav className="flex flex-col gap-1">
                  {[{ label: 'Home', path: '/' }, ...navLinks].map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`text-sm font-medium py-3 px-3 rounded-md transition-colors ${
                        isActive(link.path)
                          ? 'text-[#3B82A0] bg-slate-50'
                          : 'text-slate-600 hover:text-[#0B1D33] hover:bg-slate-50'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                <Link to="/contact" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full bg-[#0B1D33] hover:bg-[#162D4A] text-white h-11">
                    Engage Our Experts
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
